/**
 * UsageChip — compact tier-limit meter for tenant activity page headers.
 *
 * Shows "<used>/<limit> · <remaining> left" for one quota (contacts, agents,
 * users, roles, storage, aiTokens…) with a thin progress bar that turns amber at
 * ≥80% and red at ≥100%, so a tenant always sees how much of their plan tier they
 * have consumed and how much is left. Renders "Unlimited" when the tier is uncapped.
 *
 * Data is shared via useUsageLimits (one fetch across every chip on the page).
 */
import { Box, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import { useUsageLimits, UsageResource } from '@/hooks/useUsageLimits';
import { formatTokensCompact } from '@/utils/formatTokens.util';

type Tone = 'ok' | 'warning' | 'error' | 'unlimited';

interface UsageChipProps {
  resource: UsageResource;
  label: string;
  /** 'bytes' formats storage as MB/GB; 'tokens' as 10M / 100K; default 'number' uses locale grouping. */
  format?: 'number' | 'bytes' | 'tokens';
}

function fmtNumber(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtBytes(n: number): string {
  const gb = n / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  const mb = n / 1024 ** 2;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

function chipSx(tone: Tone) {
  const borderColor =
    tone === 'error' ? 'error.main' : tone === 'warning' ? 'warning.main' : 'divider';
  return {
    minWidth: 172,
    maxWidth: 260,
    px: 1.5,
    py: 0.75,
    border: '1px solid',
    borderColor,
    borderRadius: 2,
    bgcolor: 'background.paper',
  };
}

export default function UsageChip({ resource, label, format = 'number' }: UsageChipProps) {
  const { usage, loading } = useUsageLimits();
  const stat = usage?.[resource];

  if (!stat && loading) {
    return <Skeleton variant="rounded" width={190} height={40} sx={{ borderRadius: 2 }} />;
  }
  if (!stat) return null;

  const fmt = format === 'bytes' ? fmtBytes : format === 'tokens' ? formatTokensCompact : fmtNumber;

  // Uncapped tier — surface it explicitly so the tenant knows there is no cap.
  if (stat.unbounded || stat.limit <= 0) {
    return (
      <Box sx={chipSx('unlimited')}>
        <Typography variant="caption" fontWeight={700} noWrap display="block">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {fmt(stat.used)} used · Unlimited
        </Typography>
      </Box>
    );
  }

  const pct = Math.min(
    100,
    Math.max(0, stat.usagePercent ?? (stat.limit > 0 ? (stat.used / stat.limit) * 100 : 0)),
  );
  const tone: 'ok' | 'warning' | 'error' =
    stat.exceeded || pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'ok';
  const remaining = Math.max(0, stat.remaining);

  return (
    <Tooltip
      title={`${label}: ${fmt(stat.used)} of ${fmt(stat.limit)} used — ${fmt(
        remaining,
      )} remaining on your current plan`}
    >
      <Box sx={chipSx(tone)}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" fontWeight={700} noWrap>
            {label}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            color={tone === 'error' ? 'error.main' : tone === 'warning' ? 'warning.main' : 'text.secondary'}
          >
            {fmt(stat.used)}/{fmt(stat.limit)} · {fmt(remaining)} left
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={tone === 'ok' ? 'primary' : tone}
          sx={{ height: 5, borderRadius: 3, mt: 0.5 }}
        />
      </Box>
    </Tooltip>
  );
}
